import React from 'react';
import { Paperclip } from 'lucide-react';
import { COST_SOURCE_LABELS, CostSource, isQuoteCost } from '../../../Helpers/maintenanceCostUtils';
import './CostSourceChip.css';

interface CostSourceChipProps {
    /** undefined = schablon */
    source: CostSource | undefined;
    /** Rendered inside the chip, e.g. "12 500 kr" */
    children: React.ReactNode;
    hasAttachments?: boolean;
    muted?: boolean;
}

/**
 * The cost of a plan row, marked by what it rests on: green for kostnad enligt
 * offert (verified), blue for schablon, with "anp." for anpassad schablon and a
 * paperclip when files are attached.
 */
export const CostSourceChip: React.FC<CostSourceChipProps> = ({ source, children, hasAttachments = false, muted = false }) => {
    const resolved: CostSource = source ?? 'template';
    const label = COST_SOURCE_LABELS[resolved] + (hasAttachments ? ', med bilaga' : '');
    return (
        <span
            className={`cost-chip ${isQuoteCost(resolved) ? 'cost-chip--quote' : 'cost-chip--standard'}${muted ? ' cost-chip--muted' : ''}`}
            data-cost-source={resolved}
            data-testid="cost-chip"
        >
            <span className="cost-chip__amount">{children}</span>
            {resolved === 'customRate' && <span className="cost-chip__marker" aria-hidden="true">anp.</span>}
            {hasAttachments && <Paperclip size={12} aria-hidden="true" />}
            <span className="visually-hidden">({label})</span>
        </span>
    );
};

/** One line explaining the chip colours, shown above a plan's rows */
export const CostSourceLegend: React.FC<{ className?: string }> = ({ className }) => (
    <div className={`cost-source-legend ${className ?? ''}`}>
        <span className="cost-chip cost-chip--quote">Offert</span>
        <span>verifierad kostnad</span>
        <span className="cost-chip cost-chip--standard">Schablon</span>
        <span>
            uppskattning (<span className="cost-chip__marker">anp.</span> = anpassat à-pris)
        </span>
        <span className="d-inline-flex align-items-center gap-1">
            <Paperclip size={12} aria-hidden="true" /> bilaga
        </span>
    </div>
);
